import React, { useEffect, useState } from 'react';
import { normalizeAvailability, resolveZone } from '../dateUtils';
import { saveProfile, uploadProfilePhoto, doubledSubjects } from '../profileSave';
import ProfileFields from './ProfileFields';
import { saveMeta, SIGNUP_SOURCES, isSignupSource } from '../privateMeta';
import { arrival, clearArrival } from '../arrival';
import { readInvite, connectInvite } from '../invites';
import { Avatar } from './ui';
import { toast } from './feedback';

// First-run setup (brief 020, review F21): five short steps instead of the long
// Profile form. Shown until the profile can be matched; the profile is saved at the
// end of step 4, so the optional photo step can be skipped.
const STEPS = ['About you', 'Exam & subjects', 'Languages', 'Your week', 'Photo'];

const START = { name: '', age: '', gender: '', status: 'medical_student', yearLevel: '', school: '', country: '', city: '', studyCategory: '', examMonth: '', strengths: [], wantsToLearn: [], languages: ['English'], availabilityLocal: [], timezone: '' };

// The fields each step needs, with the message shown under the field when it's missing.
export function stepErrors(step, p) {
  const e = {};
  if (step === 0) {
    if (!p.name.trim()) e.name = 'Enter your name.';
    if (!p.age) e.age = 'Enter your age.';
    else if (parseInt(p.age, 10) < 18) e.age = 'You must be at least 18.';
    if (!p.gender) e.gender = 'Choose one.';
    if (!p.yearLevel) e.yearLevel = 'Choose your year.';
    if (!p.school.trim()) e.school = 'Enter your school or hospital.';
    if (!p.country) e.country = 'Choose your country.';
    if (!p.city.trim()) e.city = 'Enter your city.';
  } else if (step === 1) {
    if (!p.studyCategory) e.studyCategory = 'Choose what you are studying for.';
    if (!p.strengths.length) e.strengths = 'Pick at least one subject you can teach.';
    if (!p.wantsToLearn.length) e.wantsToLearn = 'Pick at least one subject you want to learn.';
    else if (doubledSubjects(p).length) e.wantsToLearn = `${doubledSubjects(p).join(', ')} can't be in both lists. Untick it in one of them.`;
  } else if (step === 2) {
    if (!p.languages.length) e.languages = 'Pick at least one language.';
  } else if (step === 3) {
    if (!p.availabilityLocal.length) e.availabilityLocal = 'Tap at least one time you are free.';
  }
  return e;
}

// The setup form after the saved profile loads: saved values fill fields that are still
// at their starting value; anything already typed or picked here is kept.
export function fromSavedProfile(prev, saved, displayName = '') {
  const { local } = normalizeAvailability(saved);
  const loaded = {
    ...Object.fromEntries(Object.keys(START).map(k => [k, saved[k] ?? START[k]])),
    name: saved.name || displayName || '',
    age: saved.age || '',
    languages: saved.languages?.length ? saved.languages : ['English'],
    availabilityLocal: local,
    timezone: resolveZone(saved.timezone),
  };
  const untouched = k => JSON.stringify(prev[k]) === JSON.stringify(START[k]);
  return {
    ...prev,
    ...Object.fromEntries(Object.keys(loaded).map(k => [k, untouched(k) ? loaded[k] : prev[k]])),
    // The zone is detected rather than typed, so the saved one wins.
    timezone: saved.timezone ? loaded.timezone : prev.timezone || loaded.timezone,
  };
}

function Onboarding({ user, userProfile, onDone, onLogout }) {
  const [step, setStep] = useState(0);
  const [p, setP] = useState(START);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  // Prefilled from how they arrived: an invite link or a shared link (P-3).
  const [source, setSource] = useState(() => { const a = arrival(); return a.invite ? 'invite' : a.ref === 'share' ? 'word_of_mouth' : ''; });

  // Through an invite (P-1): start on the inviter's exam; they can change it.
  useEffect(() => {
    const { invite } = arrival();
    if (!invite) return;
    readInvite(invite).then(i => { if (i?.category) setP(prev => (prev.studyCategory ? prev : { ...prev, studyCategory: i.category })); });
  }, []);

  // Start from whatever the account already has (the sign-up name, or a half-filled profile).
  // Right after sign-up the profile can arrive after someone has started typing, so the
  // saved values fill only fields still at their starting value (fromSavedProfile).
  useEffect(() => {
    if (!userProfile) return;
    setP(prev => fromSavedProfile(prev, userProfile, user.displayName));
    setPhoto(userProfile.profilePicture || '');
    // Only when the account first loads: later snapshots must not overwrite what's typed.
  }, [userProfile?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => { setP(prev => ({ ...prev, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  // The timezone comes from the device (shown on step 4 with Change), not the country.
  const setCountry = c => setP(prev => ({ ...prev, country: c, city: '' }));

  const next = async () => {
    const e = stepErrors(step, p);
    setErrors(e);
    if (Object.keys(e).length) return;
    if (step === 3) {
      setSaving(true);
      try {
        await saveProfile({ ...p, profileComplete: true }, user.uid);
        // Joined through an invite (P-1): say hi to the inviter, once.
        connectInvite(user.uid, p.name.trim()).catch(err => console.warn('invite connect failed', err));
      } catch (err) {
        console.error(err);
        toast('Could not save your profile. Check your connection and try again.', { type: 'error' });
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    if (step === STEPS.length - 1) {
      if (isSignupSource(source)) await saveMeta(user.uid, { signupSource: source }).catch(err => console.warn('signup source not saved', err));
      clearArrival();
      onDone();
      return;
    }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const back = () => { setErrors({}); setStep(s => Math.max(0, s - 1)); window.scrollTo(0, 0); };

  const pickPhoto = async ev => {
    const file = ev.target.files[0];
    ev.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    try { setPhoto(await uploadProfilePhoto(user.uid, file)); }
    catch (err) { toast('Could not upload that photo: ' + (err.message || 'please try another image.'), { type: 'error' }); }
    finally { setPhotoBusy(false); }
  };

  return (
    <div className="onboarding">
      <header className="onboarding-head">
        <img src="/logo.svg" alt="MedXMeet" className="onboarding-logo" />
        <button type="button" className="btn-text" onClick={onLogout}>Log out</button>
      </header>

      <div className="onboarding-progress" role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={step + 1} aria-label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}`}>
        {STEPS.map((s, i) => <span key={s} className={`onboarding-dot ${i === step ? 'current' : i < step ? 'done' : ''}`} />)}
      </div>

      <main className="onboarding-body">
        <div className="section-label">Step {step + 1} of {STEPS.length}</div>
        <h1 className="onboarding-title">{STEPS[step]}</h1>

        {step < 4 && <ProfileFields step={step} p={p} set={set} setCountry={c => { setCountry(c); setErrors(er => ({ ...er, country: undefined })); }} errors={errors} />}

        {step === 4 && (
          <div className="onboarding-photo">
            <p className="muted onboarding-lead">Optional. Partners are more likely to reply when they can see who they're studying with.</p>
            <Avatar src={photo} name={p.name} size={120} />
            <label className="btn-small secondary">
              {photoBusy ? 'Uploading…' : photo ? 'Change photo' : 'Add a photo'}
              <input type="file" accept="image/*" onChange={pickPhoto} disabled={photoBusy} hidden />
            </label>
            {/* Self-reported and optional (P-3); stored privately, never on the profile. */}
            <div className="form-group onboarding-source">
              <label htmlFor="ob-source">How did you hear about MedXMeet? (optional)</label>
              <select id="ob-source" value={source} onChange={e => setSource(e.target.value)}>
                <option value="">Choose one</option>
                {SIGNUP_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        )}
      </main>

      <footer className="onboarding-foot">
        {step > 0 ? <button type="button" className="btn-text" onClick={back} disabled={saving}>Back</button> : <span />}
        <button type="button" className="btn" onClick={next} disabled={saving || photoBusy}>
          {saving ? 'Saving…' : step === 4 ? (photo ? 'Find my matches' : 'Skip for now') : 'Continue'}
        </button>
      </footer>
    </div>
  );
}
export default Onboarding;
